import { Fragment, ReactNode, useEffect } from 'react';
import { useRouter } from 'next/router';

import { AppSlideFooter } from '@playbooks/components/app/app-slide-footer';
import { MktLogo } from '@playbooks/components/mkt/mkt-logo';
import { marketingMenus } from '@playbooks/components/mkt/mkt-menu-config';
import { WorkspaceMenuSlide } from '@playbooks/components/workspace/workspace-menu-slide';
import { useClient, useSession } from '@playbooks/contexts';
import { Button } from '@playbooks/ui/components/button';
import { Icon } from '@playbooks/ui/components/icon';
import { Link } from '@playbooks/ui/components/link';
import { Sheet, SheetContent, SheetHeader } from '@playbooks/ui/components/sheet';

type MktSlideProps = {
	target?: string;
	open: boolean;
	setOpen: (open: boolean) => void;
	children?: ReactNode;
};

const MktSlide = ({ target, open, setOpen, children }: MktSlideProps) => {
	const router = useRouter();
	const session = useSession();
	const { platformHref } = useClient();

	// Hooks
	useEffect(() => {
		setOpen(false);
	}, [router.asPath]);

	// Methods
	const onClose = () => setOpen(false);
	const onToggle = () => setOpen(!open);

	// Render
	return (
		<Fragment>
			<Sheet open={open} onOpenChange={nextOpen => !nextOpen && onClose()}>
				<SheetContent
					side='left'
					showCloseButton
					className='data-[side=left]:w-72 sm:data-[side=left]:max-w-72 max-w-[calc(100vw-2rem)] gap-0 bg-canvas p-0'>
					<SheetHeader>
						<MktLogo />
					</SheetHeader>

					<div className='min-h-0 grow overflow-y-auto p-2 custom-scrollbar'>
						<ul>
							{marketingMenus.map(menu => (
								<Fragment key={menu.value}>
									<li className='px-3 py-2 text-xs font-medium tracking-wide text-foreground-body'>{menu.label}</li>
									{menu.groups
										.flatMap(group => group.items)
										.map(item => (
											<li key={item.href}>
												<Link
													variant='ghost'
													href={item.target === '_blank' ? item.href : platformHref(item.href)}
													target={item.target}
													rel={item.target === '_blank' ? 'noopener noreferrer' : undefined}
													className='w-full justify-start font-normal'>
													<Icon type={item.iconType || 'far'} icon={item.icon} aria-hidden />
													{item.label}
												</Link>
											</li>
										))}
								</Fragment>
							))}

							<li className='px-3 py-2 text-xs font-medium tracking-wide text-foreground-body'>More</li>
							<li>
								<Link
									variant='ghost'
									prevIcon='money-bill-wave'
									href={platformHref('/pricing')}
									className='w-full justify-start font-normal'>
									Pricing
								</Link>
							</li>
							<li>
								<Link
									variant='ghost'
									prevIcon='headset'
									href={platformHref('/contact')}
									className='w-full justify-start font-normal'>
									Contact
								</Link>
							</li>
							<li className='px-3 py-2 text-xs font-medium tracking-wide text-foreground-body'>Account</li>
							{session?.user?.id ? (
								<Fragment>
									<li>
										<Link
											variant='ghost'
											prevIcon='home'
											href={platformHref('/home')}
											className='w-full justify-start font-normal'>
											View App
										</Link>
									</li>
									<li>
										<WorkspaceMenuSlide target={target} />
									</li>
								</Fragment>
							) : (
								<li>
									<Link
										variant='ghost'
										prevIcon='right-to-bracket'
										href={platformHref('/auth')}
										className='w-full justify-start font-normal'>
										Sign in
									</Link>
								</li>
							)}
						</ul>
					</div>
					{session?.user?.id && <AppSlideFooter />}
				</SheetContent>
			</Sheet>
			{children ? (
				children
			) : (
				<Button variant='outline' size='icon' aria-label='Open navigation' onClick={onToggle}>
					<Icon data-icon='inline-start' type='far' icon='bars' aria-hidden />
				</Button>
			)}
		</Fragment>
	);
};

export { MktSlide };
